import React, { useState, useEffect } from 'react'; // Add useEffect
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, MessageSquare, Star, Settings, LogOut, Menu, Home, X, Calendar, ArrowLeft } from 'lucide-react'; // Add ArrowLeft
import { Button } from './ui/button';
import { OwnerMobileNavbar } from './OwnerMobileNavbar';
import AvatarCircle from "./AvatarCircle";

export function OwnerLayout({ children, user, onLogout }) {
  // Initialize showMobileMenu from localStorage
  const [showMobileMenu, setShowMobileMenu] = useState(() => {
    const savedState = localStorage.getItem('showMobileMenu');
    return savedState ? JSON.parse(savedState) : false;
  });

  const location = useLocation();
  const navigate = useNavigate();

  // Persist showMobileMenu to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('showMobileMenu', JSON.stringify(showMobileMenu));
  }, [showMobileMenu]);

  const toggleMobileMenu = () => {
    setShowMobileMenu((prev) => !prev);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const handleRefresh = () => {
    navigate(location.pathname);
  };

  return (
    <div className="min-h-screen bg-gray-100 relative">
      {/* Mobile Header - Added sticky positioning */}
      <div className="lg:hidden flex items-center justify-between h-[56px] px-4 bg-black text-white sticky top-0 z-50 shadow-md">
        <div className="flex items-center">
          {/* Show Back button on /owner/bookings */}
          {location.pathname === '/owner/bookings' ? (
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-white -ml-2 h-[40px] w-[40px] p-0 flex items-center justify-center"
            >
              <ArrowLeft className="h-[24px] w-[24px]" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={toggleMobileMenu}
              className="text-white -ml-2 h-[40px] w-[40px] p-0 flex items-center justify-center"
            >
              <Menu className="h-[24px] w-[24px]" />
            </Button>
          )}
        </div>
        <div className="flex-1 text-center">
          <button
            onClick={handleRefresh}
            className="text-2xl font-bold hover:text-gray-200 transition-colors"
          >
            VillaKosh
          </button>
        </div>
        <div className="w-10 h-8">
          {user && (
            <AvatarCircle
              src={user.photoURL}
              name={user.displayName || user.email}
              className="h-8 w-8 pointer-events-none"
              textClassName="text-xs"
            />
          )}
        </div>
      </div>

      {/* Sidebar - Only visible on desktop */}
      <div className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-white shadow-lg z-40">
        <div className="p-6 flex items-center">
          <button
            onClick={handleRefresh}
            className="text-2xl font-bold hover:text-gray-600 transition-colors"
          >
            VillaKosh
          </button>
        </div>
        <nav className="mt-6">
          <div className="px-4 space-y-2">
            <Link
              to="/owner"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                isActivePath('/owner')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <LayoutDashboard className="h-5 w-5" />
              <span className="font-medium">Dashboard</span>
            </Link>
            <Link
              to="/owner/reviews"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                isActivePath('/owner/reviews')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Star className="h-5 w-5" />
              <span className="font-medium">Reviews</span>
            </Link>
            <Link
              to="/owner/bookings"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                isActivePath('/owner/bookings')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Calendar className="h-5 w-5" />
              <span className="font-medium">Bookings</span>
            </Link>
            <Link
              to="/owner/chats"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                isActivePath('/owner/chats')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <MessageSquare className="h-5 w-5" />
              <span className="font-medium">Chats</span>
            </Link>
            <Link
              to="/owner/management"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                isActivePath('/owner/management')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Settings className="h-5 w-5" />
              <span className="font-medium">Management</span>
            </Link>
            <button
              onClick={onLogout}
              className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg w-full"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
            onClick={toggleMobileMenu}
          />
          <div className="fixed left-0 top-0 h-full w-64 bg-white transform translate-x-0 transition-transform duration-200 ease-in-out z-40 lg:hidden">
            <div className="p-6 flex items-center justify-between">
              <button
                onClick={() => {
                  handleRefresh();
                  toggleMobileMenu();
                }}
                className="text-2xl text-black font-bold hover:text-gray-600 transition-colors"
              >
                VillaKosh
              </button>
              <Button variant="ghost" onClick={toggleMobileMenu}>
                <X className="h-6 w-6" />
              </Button>
            </div>
            <nav className="mt-6 flex flex-col h-[calc(100%-88px)]">
              <div className="px-4 space-y-2 flex-1">
                <Link
                  to="/owner"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActivePath('/owner')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  <LayoutDashboard className="h-5 w-5" />
                  <span className="font-medium">Dashboard</span>
                </Link>
                <Link
                  to="/owner/reviews"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActivePath('/owner/reviews')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  <Star className="h-5 w-5" />
                  <span className="font-medium">Reviews</span>
                </Link>
                <Link
                  to="/owner/bookings"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActivePath('/owner/bookings')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  <Calendar className="h-5 w-5" />
                  <span className="font-medium">Bookings</span>
                </Link>
                <Link
                  to="/owner/chats"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActivePath('/owner/chats')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  <MessageSquare className="h-5 w-5" />
                  <span className="font-medium">Chats</span>
                </Link>
                <Link
                  to="/owner/management"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActivePath('/owner/management')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  <Settings className="h-5 w-5" />
                  <span className="font-medium">Management</span>
                </Link>
              </div>

              {/* Logout Button at bottom */}
              <div className="px-4 py-4 border-t">
                <button
                  onClick={() => {
                    toggleMobileMenu();
                    onLogout();
                  }}
                  className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg w-full"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Add padding to the content area to prevent overlap with fixed header on mobile */}
      <div className="lg:ml-64 min-h-screen pb-16 lg:pb-0">
        <div className="max-w-6xl mx-auto p-4 lg:p-8">
          {children}
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden">
        <OwnerMobileNavbar user={user} />
      </div>
    </div>
  );
}